import { useEnabledFeatures } from '../../../../common/hooks/enabled-features/use-enabled-features';
import { Divider, Flex, VStack, Box, IconButton, Text, DataList, DataItem } from '@cad-arc';
import { memo } from 'react';
import { useEsriMapView } from '../../../hooks/use-esri-map-view';
import { isValidLatLng } from '../../../../location/helpers';
import eventAssignedInactive from '../../../../esri/images/event-icons/assigned/event_assigned_inactive.svg';
import { EntityAddress } from '../entity-address';
import { EventGeneralInfoBlock } from '../event-general-info-block';
import { CommonSize, getOneSizeSmaller } from '../../../../common/helpers/utils';
import { getDataItemText } from '../../../../common/helpers/component-utils';

interface Props {
    eventId: number;
    eventNumber: string;
    eventPriority: string;
    eventLocationNotes?: string;
    eventNarrative?: string;
    cfsCode: string;
    cfsDescription: string;
    placeName?: string;
    locationSource?: string;
    isVerifiedLocation?: boolean;
    displayLineFirst?: string;
    displayLineLast?: string;
    displayLineOptionalMiddle?: string;
    eventDisplayLineSubPremise?: string;
    latitudeCoordinate?: number;
    longitudeCoordinate?: number;
    isGpsLocation?: boolean;
    size?: CommonSize;
}

export const UnitAssignedSection = memo(function UnitAssignedSection({
    eventId,
    eventNumber,
    eventPriority,
    eventLocationNotes,
    eventNarrative,
    cfsCode,
    cfsDescription,
    placeName,
    locationSource,
    isVerifiedLocation,
    displayLineFirst,
    displayLineLast,
    displayLineOptionalMiddle,
    eventDisplayLineSubPremise,
    latitudeCoordinate,
    longitudeCoordinate,
    isGpsLocation,
    size,
}: Props): React.ReactElement {
    const { subpremiseSupport } = useEnabledFeatures();
    const { goTo } = useEsriMapView();

    const isGoToLocationBtnShown = isValidLatLng(latitudeCoordinate, longitudeCoordinate);

    const handleLocationButtonClick = () => {
        if (isVerifiedLocation && isGoToLocationBtnShown && goTo) {
            goTo({ latitude: latitudeCoordinate!, longitude: longitudeCoordinate! });
        }
    };

    const smallerSize = getOneSizeSmaller(size);

    return (
        <Box data-testid="MAP_UNIT_ASSIGNED_SECTION">
            <Flex direction="column" padding="12px 18px">
                <Flex alignItems="center" justifyContent="space-between" marginBottom={3}>
                    <Text fontSize={size} fontWeight="bold">
                        Assigned
                    </Text>
                    {isGoToLocationBtnShown && (
                        <IconButton
                            aria-label="Location"
                            icon="Crosshairs"
                            variant="ghost"
                            size="sm"
                            onClick={handleLocationButtonClick}
                        />
                    )}
                </Flex>
                <VStack marginBottom={3}>
                    <EventGeneralInfoBlock
                        showCloseIconButton={false}
                        eventIcon={eventAssignedInactive}
                        eventPriority={eventPriority}
                        agencyEventNumber={eventNumber}
                        cfsCode={cfsCode}
                        cfsDescription={cfsDescription}
                        eventId={eventId!}
                        isAssigned={true}
                        size={size}
                    />
                </VStack>
                <EntityAddress
                    isGpsLocation={isGpsLocation}
                    shouldSearchReverseGeocodeLocation={false}
                    source={locationSource}
                    placeName={placeName}
                    areGeoLocationCoordinatesVerified={isVerifiedLocation}
                    displayLineFirst={displayLineFirst}
                    displayLineLast={displayLineLast}
                    displayLineOptionalMiddle={displayLineOptionalMiddle}
                    subpremiseSupport={subpremiseSupport}
                    displayLineSubPremises={eventDisplayLineSubPremise}
                    size={size}
                />
                <DataList direction="column" variant="stacked" spacing={3}>
                    <DataItem
                        dataKey={<Text fontSize={smallerSize}>Location Notes</Text>}
                        style={{ wordBreak: 'break-word' }}
                    >
                        {getDataItemText(size, eventLocationNotes)}
                    </DataItem>
                    <DataItem
                        dataKey={<Text fontSize={smallerSize}>Narrative</Text>}
                        style={{ wordBreak: 'break-word' }}
                    >
                        {getDataItemText(size, eventNarrative)}
                    </DataItem>
                </DataList>
            </Flex>
            <Divider />
        </Box>
    );
});
